import Lenis from "lenis";
import gsap from "./gsap";
import { appConfig } from "@lib/config";
import { handleEditor } from "@webflow/detect-editor";

type SubscriberFn = (data: {
  scroll: number;
  limit: number;
  progress: number;
  velocity: number;
  time: number;
}) => void;

/** Native scroll passthrough – verwendet Browser-Scrolling wenn smoothScroll deaktiviert ist */
class NativeScrollPassthrough {
  #subscribers: { fn: SubscriberFn; priority: number; id: symbol }[] = [];
  #rafId: number | null = null;
  #lastScroll = 0;
  #lastTime = 0;
  #velocity = 0;
  #running = false;

  get scroll() {
    return typeof window !== "undefined" ? window.scrollY ?? window.pageYOffset : 0;
  }

  get limit() {
    if (typeof document === "undefined") return 0;
    return Math.max(
      0,
      document.documentElement.scrollHeight - window.innerHeight
    );
  }

  get progress() {
    const limit = this.limit;
    return limit > 0 ? this.scroll / limit : 0;
  }

  get velocity() {
    return this.#velocity;
  }

  add(fn: SubscriberFn, priority = 0, id = Symbol()) {
    const index = this.#subscribers.findIndex((sub) => sub.priority > priority);
    if (index === -1) {
      this.#subscribers.push({ fn, priority, id });
    } else {
      this.#subscribers.splice(index, 0, { fn, priority, id });
    }
    return () => this.remove(id);
  }

  remove(id: symbol) {
    this.#subscribers = this.#subscribers.filter((f) => f.id !== id);
  }

  #tick = () => {
    const scroll = this.scroll;
    const limit = this.limit;
    const now = performance.now() / 1000;
    const dt = now - this.#lastTime;
    this.#velocity = dt > 0 ? (scroll - this.#lastScroll) / dt : 0;
    this.#lastScroll = scroll;
    this.#lastTime = now;

    const data = {
      scroll,
      limit,
      progress: limit > 0 ? scroll / limit : 0,
      velocity: this.#velocity,
      time: now,
    };
    this.#subscribers.forEach((f) => f.fn(data));
    this.#rafId = requestAnimationFrame(this.#tick);
  };

  scrollTo(offset: number, options?: { immediate?: boolean }) {
    window.scrollTo({
      top: offset,
      behavior: options?.immediate ? "auto" : "smooth",
    });
  }

  toTop() {
    window.scrollTo({ top: 0, behavior: "auto" });
  }

  resize() {
    // No-op für natives Scroll
  }

  destroy() {
    this.stop();
    this.#subscribers = [];
  }

  start() {
    if (this.#running) return;
    this.#running = true;
    this.#lastScroll = this.scroll;
    this.#lastTime = performance.now() / 1000;
    this.#rafId = requestAnimationFrame(this.#tick);
  }

  stop() {
    if (!this.#running) return;
    this.#running = false;
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }
}

const SCROLL_CONFIG = {
  infinite: false,
  lerp: 0.1,
  smoothWheel: true,
  touchMultiplier: 2,
};

class LenisScroll extends Lenis {
  #ticker = gsap.ticker.add((time: number) => this.raf(time * 1000));
  #subscribers: { fn: SubscriberFn; priority: number; id: symbol }[] = [];

  constructor() {
    super(SCROLL_CONFIG);
    this.on("scroll", this.#scroll.bind(this));
  }

  #scroll(data: any) {
    this.notify(data);
  }

  toTop() {
    this.scrollTo(0, { immediate: true });
  }

  add(fn: SubscriberFn, priority = 0, id = Symbol()) {
    const index = this.#subscribers.findIndex((sub) => sub.priority > priority);
    if (index === -1) {
      this.#subscribers.push({ fn, priority, id });
    } else {
      this.#subscribers.splice(index, 0, { fn, priority, id });
    }
    return () => this.remove(id);
  }

  remove(id: symbol) {
    this.#subscribers = this.#subscribers.filter((f) => f.id !== id);
  }

  notify(data: any) {
    if (this.#subscribers.length < 1) return;
    this.#subscribers.forEach((f) => f.fn(data));
  }
}

function createScroll() {
  if (appConfig.smoothScroll === false) {
    const scroll = new NativeScrollPassthrough();
    handleEditor((isEditor) => {
      if (!isEditor) {
        scroll.start();
      }
    });
    return scroll;
  }

  const scroll = new LenisScroll();
  handleEditor((isEditor) => {
    if (isEditor) {
      scroll.destroy();
    } else {
      scroll.start();
    }
  });
  return scroll;
}

export const Scroll = createScroll();
